import '@classroom-ai/ui/global.css';

import { useEffect } from 'react';
import { usePathname } from '@classroom-ai/ui/routes/hooks';
import { themeConfig, ThemeProvider } from '@classroom-ai/ui/theme';
import { ProgressBar } from '@classroom-ai/ui/components/progress-bar';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MotionLazy } from '@classroom-ai/ui/components/animate/motion-lazy';
import { SettingsDrawer, SettingsProvider, createDefaultSettings } from '@classroom-ai/ui/components/settings';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { CONFIG } from 'src/global-config';

import { AuthProvider } from 'src/auth/context/firebase';

const queryClient = new QueryClient();

const defaultSettings = createDefaultSettings(CONFIG.appVersion);

// ----------------------------------------------------------------------

type AppProps = {
  children: React.ReactNode;
};

export default function App({ children }: AppProps) {
  useScrollToTop();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SettingsProvider defaultSettings={defaultSettings}>
          <ThemeProvider
            modeStorageKey={themeConfig.modeStorageKey}
            defaultMode={themeConfig.defaultMode}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MotionLazy>
                <ProgressBar />
                <SettingsDrawer defaultSettings={defaultSettings} />
                {children}
              </MotionLazy>
            </LocalizationProvider>
          </ThemeProvider>
        </SettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// ----------------------------------------------------------------------

function useScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
